import { ReactNode } from 'react';
import styled from 'styled-components';

import Tooltip from 'components/shared/Tooltip';
import { useClickAttachment } from 'hooks/useClickAttachment';
import Upload from 'hooks/useDirectUpload/Upload';
import Attachment from 'models/Attachment';

// "entry" can be an object where the "id" prop is a number or a GUID. We only
// want to make the link clickable and fetch the attachment if the "id" prop
// is a GUID.
const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

type DocumentLinkInput = {
  backgroundColor?: string;
  children: Iterable<ReactNode> | ReactNode;
  entry: Attachment | Upload;
  disabled?: boolean;
};

export default function DocumentLink(props: DocumentLinkInput) {
  const { backgroundColor, entry, children, disabled = false } = props;

  const deletedByClient = entry.fileDeletedByClient;
  const isDisabled = !!deletedByClient || disabled;
  const openAttachment = useClickAttachment(entry.id);

  const handleClick = () => {
    if (!guidRegex.test(entry.id) || isDisabled) return;

    openAttachment();
  };

  const deletedFileToolTip = `This file is no longer accessible due to ${deletedByClient?.name} file security policy`;

  return (
    <Tooltip text={deletedFileToolTip} disabled={!deletedByClient}>
      <Link
        backgroundColor={backgroundColor}
        onClick={handleClick}
        disabled={isDisabled}
        clickable={guidRegex.test(entry.id)}>
        {children}
      </Link>
    </Tooltip>
  );
}

const Link = styled.a<{
  backgroundColor?: string;
  disabled: boolean;
  clickable: boolean;
}>`
  display: block;
  cursor: ${(props) => (props.clickable ? 'pointer' : 'default')};
  cursor: ${(props) => props.disabled && 'not-allowed'};
  opacity: ${(props) => props.disabled && 0.25};
`;
